/**
 * Step-up OTP dialog.
 *
 * Mounted once in App. It registers itself as the step-up prompt, so a 403
 * STEP_UP_REQUIRED from the API client opens it (see lib/step-up.ts). When
 * it opens, it asks the backend to send a code to the account phone. A
 * correct code resolves the prompt with true, and the API client sends the
 * refused request again. Cancel resolves it with false.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { StudioButton } from "@/features/studio/theme";
import { registerStepUpPrompt } from "@/lib/step-up";
import {
	getRetryAfterSeconds,
	isCodeGone,
	sendStepUpOtp,
	verifyStepUpOtp,
} from "../services/step-up.service";

const OTP_LENGTH = 6;

export function StepUpOtpDialog() {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [otp, setOtp] = useState("");
	const [sending, setSending] = useState(false);
	const [verifying, setVerifying] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const resolver = useRef<((verified: boolean) => void) | null>(null);

	const failureMessage = (err: unknown, fallbackKey: string): string => {
		const seconds = getRetryAfterSeconds(err);
		return seconds === undefined
			? t(fallbackKey)
			: t("stepUp.waitSeconds", { seconds });
	};

	const send = async () => {
		setSending(true);
		setError(null);
		try {
			await sendStepUpOtp();
		} catch (err) {
			setError(failureMessage(err, "stepUp.sendFailed"));
		} finally {
			setSending(false);
		}
	};

	// The registration reads `send` through a ref, so a new render (for
	// example a language change) does not register a second prompt.
	const sendRef = useRef(send);
	sendRef.current = send;

	useEffect(() => {
		const unregister = registerStepUpPrompt(
			() =>
				new Promise<boolean>((resolve) => {
					resolver.current = resolve;
					setOtp("");
					setError(null);
					setOpen(true);
					void sendRef.current();
				}),
		);
		return () => {
			unregister();
			resolver.current?.(false);
			resolver.current = null;
		};
	}, []);

	const close = (verified: boolean) => {
		resolver.current?.(verified);
		resolver.current = null;
		setOpen(false);
	};

	const verify = async (event: React.FormEvent) => {
		event.preventDefault();
		if (otp.length !== OTP_LENGTH || verifying) return;
		setVerifying(true);
		setError(null);
		try {
			await verifyStepUpOtp(otp);
			close(true);
		} catch (err) {
			setOtp("");
			setError(
				failureMessage(
					err,
					isCodeGone(err) ? "stepUp.codeGone" : "stepUp.verifyFailed",
				),
			);
		} finally {
			setVerifying(false);
		}
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next && !verifying) close(false);
			}}
		>
			<DialogContent className="max-w-md">
				<form onSubmit={verify} className="space-y-4">
					<DialogHeader>
						<DialogTitle>{t("stepUp.title")}</DialogTitle>
						<DialogDescription>{t("stepUp.description")}</DialogDescription>
					</DialogHeader>

					<div className="space-y-1.5">
						<label
							htmlFor="step-up-otp"
							className="block text-sm font-medium text-[var(--color-dashboard-muted-dark)]"
						>
							{t("stepUp.codeLabel")}
						</label>
						<input
							id="step-up-otp"
							inputMode="numeric"
							autoComplete="one-time-code"
							value={otp}
							onChange={(e) =>
								setOtp(e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH))
							}
							maxLength={OTP_LENGTH}
							className="w-full rounded-[7px] border border-[var(--color-border-default)] bg-[var(--color-dashboard-paper)] px-3.5 py-2.5 font-mono text-sm text-[var(--color-dashboard-text)] outline-none"
						/>
						{error && (
							<p role="alert" className="text-sm text-dashboard-pill-risk">
								{error}
							</p>
						)}
						<button
							type="button"
							onClick={() => void send()}
							disabled={sending}
							className="text-sm font-medium text-[var(--color-dashboard-accent-dark)] underline-offset-2 hover:underline disabled:opacity-50"
						>
							{sending ? t("stepUp.sending") : t("stepUp.resend")}
						</button>
					</div>

					<DialogFooter className="gap-2">
						<StudioButton
							type="button"
							variant="ghost"
							onClick={() => close(false)}
							disabled={verifying}
						>
							{t("stepUp.cancel")}
						</StudioButton>
						<StudioButton
							type="submit"
							variant="primary"
							disabled={verifying || otp.length !== OTP_LENGTH}
						>
							{verifying ? t("stepUp.verifying") : t("stepUp.verify")}
						</StudioButton>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
